import { BarChart3, Download, LineChart, Target } from 'lucide-react';

import type { Role } from '@/stores/session';
import type { Guide } from '../types';

/** Insights / Analytics — mirrors the Analytics nav gate. */
const INSIGHTS_ROLES: Role[] = ['super_admin', 'founder', 'accountant'];

export const ANALYTICS_GUIDES: Guide[] = [
  {
    slug: 'analytics-overview',
    title: 'Insights at a glance',
    module: 'analytics',
    summary: 'Cross-module dashboards, KPIs & goals, and data exports.',
    icon: BarChart3,
    roles: INSIGHTS_ROLES,
    body: `**Insights** is the analytics layer — it reads across every module to show how
the business is performing, track goals, and get data out.

### What's here
- **Dashboards** — cross-module BI: pipeline, delivery and finance together.
- **KPIs & Goals** — set objectives with measurable key results and track progress.
- **Exports** — pull the underlying data out as CSV (or schedule it).

### Who sees it
Available to **leadership and finance** — the people who steer the numbers.`,
  },
  {
    slug: 'analytics-dashboards',
    title: 'Dashboards',
    module: 'analytics',
    summary: 'Cross-module BI — the business on one screen.',
    icon: LineChart,
    roles: INSIGHTS_ROLES,
    body: `**Dashboards** is the cross-module BI view — it reads from CRM, Projects and
Finance to show the headline picture in one place.

### How to use it
- Open it for a live read of pipeline, delivery and financial health.
- Use it for the regular "how are we doing?" review rather than opening each module.

### Why it's here
The numbers that matter live in different modules; Dashboards stitches them into a
single, current picture so trends are obvious and decisions are faster.`,
  },
  {
    slug: 'analytics-kpis-goals',
    title: 'KPIs & Goals',
    module: 'analytics',
    summary: 'Set objectives with key results and track progress (OKR-style).',
    icon: Target,
    roles: INSIGHTS_ROLES,
    body: `**KPIs & Goals** is OKR-style goal tracking — **objectives** broken down into
measurable **key results**.

### How to use it
1. Create an **objective** (what you want to achieve) and give it a status.
2. Add **key results** with a **target** and **current** value (and a unit).
3. Update the current value as you progress — each key result shows a **progress bar**.

### Why it's here
Goals stay honest when they're measurable and visible. KPIs & Goals turns "we want
to grow" into specific targets the whole leadership team can track.`,
  },
  {
    slug: 'analytics-exports',
    title: 'Exports',
    module: 'analytics',
    summary: 'Download key datasets as CSV — or schedule them by email.',
    icon: Download,
    roles: INSIGHTS_ROLES,
    body: `**Exports** lets you get the underlying data out for your own analysis.

### How to use it
- Pick a dataset (e.g. time entries, the executive-dashboard snapshot) and **download
  CSV**.
- Optionally **schedule** an export to be emailed on a recurring basis.
- You only see the exports your role is allowed to.

### Why it's here
Sometimes the app's views aren't enough and you need the raw rows — for a board
deck, a spreadsheet model or your accountant. Exports gives a clean, governed way
to get them.`,
  },
];
